import type { JSX } from 'react';
import {
    Box,
    Heading,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import type { TotalWeightLossState } from '@common/enums/totalWeightLossState';
import { concatOrNull, localeNlString } from '@common/utils/textUtils';

export interface ITotalWeightLossItem {
    date: Date | string;
    difference: {
        days: number;
        months: number;
    };
    isFromFollowUp?: boolean;
    median?: number;
    minMaxFirstBandwidth?: number[];
    minMaxSecondBandwidth?: number[];
    totalWeightLoss?: number;
    totalWeightLossState?: TotalWeightLossState;
    weight?: number;
}

interface ITotalWeightLossTableProps {
    items: ITotalWeightLossItem[];
}

export function TotalWeightLossTable(props: ITotalWeightLossTableProps): JSX.Element {
    const { items } = props;
    const tableItems = getFilteredAndReversedItems();
    const initialWeight = items[0].weight;

    return (
        <Box mb={6}>
            <Heading
                as="h3"
                fontWeight="medium"
                size="medium"
            >
                Tabel percentage gewichtsverlies (%TWL)
            </Heading>
            {typeof initialWeight === 'number' && <Text mb={6}>Startgewicht: {concatOrNull(initialWeight, 'kg')}</Text>}
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>Tijd na operatie</TableHeader>
                            <TableHeader>Percentage</TableHeader>
                            <TableHeader>Gewicht</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableItems.map(({ difference, isFromFollowUp, totalWeightLoss, weight }) => (
                            <TableRow key={difference.months}>
                                <TableCell>
                                    {Math.floor(difference.months)} mnd
                                    {difference.days > 0 && ` en ${difference.days} dagen`}
                                </TableCell>
                                <TableCell>
                                    {typeof totalWeightLoss === 'number' && `${localeNlString(totalWeightLoss)}%`}
                                    {isFromFollowUp && ' (OCA)'}
                                </TableCell>
                                <TableCell>{typeof weight === 'number' && concatOrNull(weight, 'kg')}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    function getFilteredAndReversedItems(): ITotalWeightLossItem[] {
        return items
            .filter(
                (totalWeightLossDistribution) =>
                    (totalWeightLossDistribution.difference.months > 0 ||
                        totalWeightLossDistribution.difference.days > 0) &&
                    totalWeightLossDistribution.weight
            )
            .reverse();
    }
}
